import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  input,
  output,
  viewChild,
  ViewEncapsulation,
} from '@angular/core';
import { AbstractControl, ValidationErrors } from '@angular/forms';
import { ControlChange } from '../../base/control-change-event';
import { HxBaseControl } from '../../base/hx-base-control';
import { HxCheckboxIndeterminate } from './checkbox-indeterminate';

export interface CheckboxOption {
  label: string;
  value: string | number;
  checked?: boolean;
  /** Whether this option is in indeterminate (partially checked) state */
  indeterminate?: boolean;
}

let nextUniqueId = 0;

/**
 * Harmony Checkbox Component
 *
 * A checkbox component following Moon Design System specifications with:
 *
 * - Three visual states: unchecked, checked, indeterminate
 * - Full Angular Forms integration via ControlValueAccessor
 * - WCAG 2.1 AA accessibility compliance
 * - Keyboard navigation (Tab, Space)
 * - Design token-based styling
 *
 * @example
 *   ```html
 *   <!-- Two-way binding with selectedValues -->
 *   <hx-checkbox
 *     [options]="[{label: 'Accept terms', value: 'accept'}]"
 *     [(selectedValues)]="selectedValues">
 *   </hx-checkbox>
 *
 *   <!-- Forms integration -->
 *   <hx-checkbox
 *     [options]="checkboxOptions"
 *     [formControl]="checkboxControl">
 *   </hx-checkbox>
 *   ```;
 */
@Component({
  selector: 'hx-checkbox',
  templateUrl: './checkbox.html',
  styleUrls: ['./checkbox.css', '../../styles/form-control-tokens.css'],
  standalone: true,
  imports: [HxCheckboxIndeterminate],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  host: {
    '[class.hx-checkbox-host]': 'true',
    '[attr.id]': 'id()',
  },
})
export class HxCheckbox extends HxBaseControl {
  /** Reference to the native input element */
  protected readonly _inputElement = viewChild.required<ElementRef<HTMLInputElement>>('input');

  /** Array of checkbox options */
  readonly options = input<CheckboxOption[]>([]);

  /** Selected values for options array */
  readonly selectedValues = input<(string | number)[]>([]);

  /** Output for selected values changes */
  readonly selectedValuesChange = output<(string | number)[]>();

  /** Custom CSS class for styling overrides */
  readonly className = input<string>('');

  /** Event emitted when checked state changes - emits ControlChange<boolean> */
  readonly change = output<ControlChange<boolean>>();

  /**
   * Handles keydown events on checkbox inputs to allow toggling via Enter key. Native checkboxes only toggle on Space;
   * Enter provides consistent keyboard interaction across assessment controls (radio buttons, chips, and checkboxes).
   *
   * @internal
   */
  _handleInputKeydown(optionValue: string | number, event: KeyboardEvent): void {
    if (this.disabled() || this.readonly()) {
      return;
    }

    if (event.key === 'Enter') {
      event.preventDefault();
      const input = event.target as HTMLInputElement;
      input.click();
      return;
    }

    if (
      event.key === 'ArrowDown' ||
      event.key === 'ArrowRight' ||
      event.key === 'ArrowUp' ||
      event.key === 'ArrowLeft'
    ) {
      this._navigateWithArrowKeys(event);
    }
  }

  /**
   * Navigates between checkboxes within the same logical group using arrow keys.
   *
   * @internal
   */
  private _navigateWithArrowKeys(event: KeyboardEvent): void {
    const currentInput = event.target as HTMLInputElement;
    if (!currentInput) {
      return;
    }

    // Determine the logical grouping container (group, fieldset, wrapper options, section, or form)
    const container =
      currentInput.closest<HTMLElement>('[role="group"], .checkbox-wrapper__options, .checkbox-wrapper, fieldset') ??
      currentInput.closest<HTMLElement>('.hx-checkbox-group') ??
      currentInput.closest<HTMLElement>('hx-sub-section, section, form') ??
      document.body;

    const checkboxes = Array.from(
      container.querySelectorAll<HTMLInputElement>('input[type="checkbox"]:not(:disabled)')
    ).filter((cb) => !cb.hidden);

    if (checkboxes.length <= 1) {
      return;
    }

    const currentIndex = checkboxes.indexOf(currentInput);
    if (currentIndex === -1) {
      return;
    }

    event.preventDefault();

    let nextIndex = currentIndex;
    if (event.key === 'ArrowDown' || event.key === 'ArrowRight') {
      nextIndex = (currentIndex + 1) % checkboxes.length;
    } else if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') {
      nextIndex = (currentIndex - 1 + checkboxes.length) % checkboxes.length;
    }

    const targetCheckbox = checkboxes[nextIndex];
    if (targetCheckbox) {
      targetCheckbox.focus();
    }
  }

  /**
   * Handles change events for option checkboxes
   *
   * @internal
   */
  _handleOptionChange(optionValue: string | number, event: Event): void {
    const input = event.target as HTMLInputElement;
    const isChecked = input.checked;

    let newSelectedValues: (string | number)[];
    if (isChecked) {
      newSelectedValues = [...this.selectedValues(), optionValue];
    } else {
      newSelectedValues = this.selectedValues().filter((v) => v !== optionValue);
    }

    this.selectedValuesChange.emit(newSelectedValues);
    this.notifyChange(newSelectedValues);
    this.markTouched();
  }

  /**
   * Check if an option is selected
   *
   * @internal
   */
  _isOptionSelected(optionValue: string | number): boolean {
    return this.selectedValues().includes(optionValue);
  }

  /** Focuses the checkbox input element */
  focus(): void {
    this._inputElement().nativeElement.focus();
  }

  // ===== HxBaseControl Overrides =====

  /** Sets the value from form model (ControlValueAccessor) */
  protected _setValue(value: any): void {
    // The checked state is managed via input binding
    // Forms will sync via writeValue -> this method
  }

  /** Validates the checkbox value (for required validation) */
  protected _validate(control: AbstractControl): ValidationErrors | null {
    // If required and no values selected, return validation error
    if (this.required() && this.selectedValues().length === 0) {
      return { required: true };
    }
    return null;
  }
}
